import { useMemo, useState } from "react";
import AppHeader from "@/components/AppHeader";
import Main from "@/components/Main";
import WorkedExample from "@/components/WorkedExample";
import Seo from "@/components/Seo";
import {
  Scale,
  Lightbulb,
  Shuffle,
  Check,
  X as XIcon,
  RefreshCw,
  ArrowRight,
  ArrowLeftRight,
  AlertCircle,
  Eye,
  EyeOff,
} from "@/lib/icons";
import {
  EQ_CATEGORY_LABELS,
  EQ_PROBLEMS,
  type EqCategory,
  type EqDifficulty,
  type EqProblem,
} from "@/data/equilibrium";

const ALL_CATEGORIES = Object.keys(EQ_CATEGORY_LABELS) as EqCategory[];
const ALL_DIFFICULTIES: EqDifficulty[] = ["easy", "medium", "hard"];

function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

interface AttemptState {
  problemId: string;
  selected: number | null;
  graded: boolean;
}

/** Stress-table row used in the lesson. */
interface StressRow {
  stress: string;
  shift: string;
  kChange: string;
}

const STRESS_TABLE: StressRow[] = [
  { stress: "Add reactant", shift: "→ toward products", kChange: "no change" },
  { stress: "Add product", shift: "← toward reactants", kChange: "no change" },
  {
    stress: "↑ Pressure (↓ V)",
    shift: "→ toward fewer mol of gas",
    kChange: "no change",
  },
  {
    stress: "↓ Pressure (↑ V)",
    shift: "→ toward more mol of gas",
    kChange: "no change",
  },
  {
    stress: "↑ T, exothermic",
    shift: "← toward reactants",
    kChange: "K decreases",
  },
  {
    stress: "↑ T, endothermic",
    shift: "→ toward products",
    kChange: "K increases",
  },
  { stress: "Add catalyst", shift: "no shift", kChange: "no change" },
  {
    stress: "Add inert gas (const V)",
    shift: "no shift",
    kChange: "no change",
  },
];

export default function Equilibrium() {
  const [enabledCategories, setEnabledCategories] = useState<Set<EqCategory>>(
    new Set(ALL_CATEGORIES),
  );
  const [difficulty, setDifficulty] = useState<EqDifficulty | "all">("all");

  const pool = useMemo(
    () =>
      EQ_PROBLEMS.filter(
        (p) =>
          enabledCategories.has(p.category) &&
          (difficulty === "all" || p.difficulty === difficulty),
      ),
    [enabledCategories, difficulty],
  );

  const [current, setCurrent] = useState<EqProblem | null>(
    () => EQ_PROBLEMS[0] ?? null,
  );
  const [attempt, setAttempt] = useState<AttemptState>({
    problemId: EQ_PROBLEMS[0]?.id ?? "",
    selected: null,
    graded: false,
  });
  const [stats, setStats] = useState({ attempted: 0, correct: 0 });
  /** IDs of problems the learner has missed at least once and not yet redeemed. */
  const [missedIds, setMissedIds] = useState<string[]>([]);
  const [reviewOpen, setReviewOpen] = useState(false);

  const next = () => {
    if (pool.length === 0) return;
    let p = pickRandom(pool);
    if (pool.length > 1 && current && p.id === current.id) {
      p = pickRandom(pool.filter((q) => q.id !== current.id));
    }
    setCurrent(p);
    setAttempt({ problemId: p.id, selected: null, graded: false });
  };

  const submit = () => {
    if (!current || attempt.selected === null) return;
    const ok = attempt.selected === current.correct;
    setAttempt((a) => ({ ...a, graded: true }));
    setStats((s) => ({
      attempted: s.attempted + 1,
      correct: s.correct + (ok ? 1 : 0),
    }));
    setMissedIds((prev) => {
      if (!current) return prev;
      if (ok) {
        // Redeem: getting it right removes it from the review list.
        return prev.filter((id) => id !== current.id);
      }
      return prev.includes(current.id) ? prev : [...prev, current.id];
    });
  };

  const missedProblems = useMemo(
    () =>
      missedIds
        .map((id) => EQ_PROBLEMS.find((p) => p.id === id))
        .filter((p): p is EqProblem => Boolean(p)),
    [missedIds],
  );

  const toggleCategory = (c: EqCategory) => {
    setEnabledCategories((prev) => {
      const next = new Set(prev);
      if (next.has(c)) {
        if (next.size > 1) next.delete(c);
      } else next.add(c);
      return next;
    });
  };

  const accuracy =
    stats.attempted === 0
      ? 0
      : Math.round((stats.correct / stats.attempted) * 100);

  return (
    <div className="min-h-screen bg-transparent">
      <Seo title="Chemical Equilibrium — ElementPad" description="Equilibrium constants, Le Chatelier’s principle, and ICE tables with worked examples and visualizations." path="/learn/equilibrium" />
      <AppHeader />
      <Main className="max-w-5xl mx-auto px-4 py-8 space-y-10">
        {/* Header */}
        <header className="space-y-2">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-primary">
            <Scale size={14} aria-hidden="true" />
            Conceptual unit
          </div>
          <h1 className="text-3xl font-bold text-foreground">
            Equilibrium &amp; Le Chatelier's Principle
          </h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Reactions don't always go to completion. Learn what dynamic
            equilibrium really means, how to write K, and how to predict the
            way a system shifts when stressed.
          </p>
        </header>

        {/* ── Lesson ──────────────────────────────────────────────────────── */}
        <section
          aria-labelledby="lesson-heading"
          className="rounded-xl border border-border bg-tile-bg p-6 space-y-6"
        >
          <div className="flex items-center gap-2">
            <Lightbulb size={16} className="text-primary" aria-hidden="true" />
            <h2
              id="lesson-heading"
              className="text-lg font-semibold text-foreground"
            >
              The big ideas
            </h2>
          </div>

          {/* Big ideas grid */}
          <ol className="grid sm:grid-cols-2 gap-3 text-sm">
            {[
              {
                t: "1. Dynamic, not static",
                d: "At equilibrium, both forward and reverse reactions still happen — but at equal rates. Concentrations stop changing.",
              },
              {
                t: "2. Closed systems only",
                d: "If anything escapes (open beaker, open flask), the reverse reaction starves and equilibrium never establishes.",
              },
              {
                t: "3. K = products / reactants",
                d: "Each species is raised to its coefficient. Pure solids and liquids are omitted (their 'concentration' is constant).",
              },
              {
                t: "4. K depends only on T",
                d: "Catalysts, initial amounts, pressure, and inert gases do NOT change K. Only temperature does.",
              },
            ].map((s) => (
              <li
                key={s.t}
                className="rounded-lg border border-border bg-background/40 p-3"
              >
                <p className="font-semibold text-foreground text-sm">{s.t}</p>
                <p className="text-xs text-muted-foreground mt-1">{s.d}</p>
              </li>
            ))}
          </ol>

          {/* Q vs K + Le Chatelier statement */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-2">
              <h3 className="text-sm font-semibold text-foreground">
                Q vs K — which way does it go?
              </h3>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>
                  <span className="font-mono text-foreground">Q &lt; K</span> →
                  not enough product yet → shift <strong>right</strong>.
                </li>
                <li>
                  <span className="font-mono text-foreground">Q = K</span> →
                  already at equilibrium.
                </li>
                <li>
                  <span className="font-mono text-foreground">Q &gt; K</span> →
                  too much product → shift <strong>left</strong>.
                </li>
              </ul>
              <p className="font-mono text-xs text-foreground pt-2 border-t border-border">
                Q has the same form as K, but with the CURRENT (non-equilibrium)
                concentrations.
              </p>
            </div>
            <div className="rounded-lg border border-border bg-background/40 p-4 space-y-2">
              <div className="flex items-center gap-2">
                <ArrowLeftRight
                  size={14}
                  className="text-primary"
                  aria-hidden="true"
                />
                <h3 className="text-sm font-semibold text-foreground">
                  Le Chatelier's Principle
                </h3>
              </div>
              <p className="text-xs text-muted-foreground">
                If a system at equilibrium is disturbed, it shifts to{" "}
                <strong>partially counteract</strong> the disturbance.
              </p>
              <p className="text-xs text-muted-foreground">
                Add a substance → system consumes some of it. Heat an exo
                reaction → system absorbs some heat by shifting back to
                reactants.
              </p>
            </div>
          </div>

          {/* Stress table */}
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-xs">
              <thead className="bg-muted/40">
                <tr className="text-left text-muted-foreground">
                  <th className="px-3 py-2 font-medium">Stress</th>
                  <th className="px-3 py-2 font-medium">Shift</th>
                  <th className="px-3 py-2 font-medium">K</th>
                </tr>
              </thead>
              <tbody className="text-foreground">
                {STRESS_TABLE.map((r) => (
                  <tr key={r.stress} className="border-t border-border">
                    <td className="px-3 py-2 font-medium">{r.stress}</td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {r.shift}
                    </td>
                    <td className="px-3 py-2 font-mono">{r.kChange}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <WorkedExample
            title="Predicting shifts: the Haber process"
            problem="For N₂(g) + 3 H₂(g) ⇌ 2 NH₃(g), ΔH = −92 kJ. Predict the direction the system shifts when (a) you add more H₂, (b) you compress the container, (c) you raise the temperature, (d) you add a catalyst."
            steps={[
              {
                label: "(a) Add H₂",
                explain:
                  "Adding a reactant stresses the left side. System consumes some H₂ to relieve the stress.",
                formula: "Shift →   (more NH₃)",
              },
              {
                label: "(b) Compress (↑P, ↓V)",
                explain:
                  "Count moles of gas: 4 mol on the left, 2 mol on the right. System favors the side with FEWER gas molecules.",
                formula: "Shift →   (toward 2 mol gas)",
              },
              {
                label: "(c) Raise T",
                explain:
                  "Forward is exothermic (ΔH < 0), so heat acts as a 'product'. Adding heat pushes back toward reactants.",
                formula: "Shift ←   K decreases",
              },
              {
                label: "(d) Add catalyst",
                explain:
                  "Speeds both directions equally. Equilibrium is reached faster, but the position and K are unchanged.",
                formula: "No shift",
              },
            ]}
            answer="(a) →,  (b) →,  (c) ←,  (d) no shift"
          />
        </section>

        {/* ── Drill ───────────────────────────────────────────────────────── */}
        <section aria-labelledby="drill-heading" className="space-y-4">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <h2
                id="drill-heading"
                className="text-lg font-semibold text-foreground"
              >
                Practice drill
              </h2>
              <p className="text-xs text-muted-foreground">
                Multiple choice, instant feedback with reasoning.
              </p>
            </div>
            <div
              className="text-xs font-mono text-muted-foreground"
              aria-live="polite"
            >
              <span className="text-foreground font-semibold">
                {stats.correct}
              </span>
              /{stats.attempted} correct
              {stats.attempted > 0 && (
                <span className="ml-2">({accuracy}% accuracy)</span>
              )}
            </div>
          </div>

          {/* Filters */}
          <div className="rounded-xl border border-border bg-tile-bg p-4 space-y-3">
            {/* Review-mistakes panel */}
            <div className="rounded-lg border border-border bg-background/40 p-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <AlertCircle
                    size={14}
                    className={
                      missedProblems.length > 0
                        ? "text-destructive"
                        : "text-muted-foreground"
                    }
                    aria-hidden="true"
                  />
                  <p className="text-xs text-foreground font-medium">
                    Review mistakes
                  </p>
                  <span className="text-[11px] font-mono text-muted-foreground">
                    {missedProblems.length} to review
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {missedProblems.length > 0 && (
                    <button
                      onClick={() => setMissedIds([])}
                      className="text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Clear
                    </button>
                  )}
                  <button
                    onClick={() => setReviewOpen((v) => !v)}
                    disabled={missedProblems.length === 0}
                    aria-expanded={reviewOpen}
                    className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-border bg-background hover:bg-accent text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {reviewOpen ? (
                      <>
                        <EyeOff size={12} aria-hidden="true" />
                        Hide
                      </>
                    ) : (
                      <>
                        <Eye size={12} aria-hidden="true" />
                        Show
                      </>
                    )}
                  </button>
                </div>
              </div>
              {missedProblems.length === 0 ? (
                <p className="text-[11px] text-muted-foreground mt-2">
                  Missed questions will collect here. Get one right again to
                  remove it from your review list.
                </p>
              ) : (
                reviewOpen && (
                  <ol className="mt-3 space-y-2">
                    {missedProblems.map((p, idx) => (
                      <li
                        key={p.id}
                        className="rounded-md border border-border bg-tile-bg/60 p-3 space-y-1.5"
                      >
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                            #{idx + 1} · {EQ_CATEGORY_LABELS[p.category]} ·{" "}
                            {p.difficulty}
                          </span>
                          <button
                            onClick={() => {
                              setCurrent(p);
                              setAttempt({
                                problemId: p.id,
                                selected: null,
                                graded: false,
                              });
                            }}
                            className="text-[11px] text-primary hover:underline"
                          >
                            Retry →
                          </button>
                        </div>
                        <p className="text-xs text-foreground leading-relaxed">
                          {p.prompt}
                        </p>
                        {p.formula && (
                          <p className="font-mono text-[11px] text-muted-foreground">
                            {p.formula}
                          </p>
                        )}
                        <p className="text-[11px] text-emerald-400 font-medium">
                          ✓ {String.fromCharCode(65 + p.correct)}.{" "}
                          {p.options[p.correct]}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          {p.insight}
                        </p>
                      </li>
                    ))}
                  </ol>
                )
              )}
            </div>

            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                Difficulty
              </p>
              <div className="flex flex-wrap gap-2">
                {(["all", ...ALL_DIFFICULTIES] as const).map((d) => (
                  <button
                    key={d}
                    onClick={() => setDifficulty(d)}
                    className={`px-3 py-1 rounded-full text-xs font-medium capitalize transition-colors ${
                      difficulty === d
                        ? "bg-foreground text-background"
                        : "bg-muted text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                Categories
              </p>
              <div className="flex flex-wrap gap-2">
                {ALL_CATEGORIES.map((c) => {
                  const on = enabledCategories.has(c);
                  return (
                    <button
                      key={c}
                      onClick={() => toggleCategory(c)}
                      aria-pressed={on}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors border ${
                        on
                          ? "bg-primary/10 border-primary/40 text-primary"
                          : "bg-muted border-transparent text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {EQ_CATEGORY_LABELS[c]}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Drill card */}
          {current ? (
            <article className="rounded-xl border border-border bg-card p-6 space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-primary px-2 py-0.5 rounded-full bg-primary/10 border border-primary/20">
                  {EQ_CATEGORY_LABELS[current.category]} · {current.difficulty}
                </span>
                <button
                  onClick={next}
                  className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                >
                  <Shuffle size={12} aria-hidden="true" />
                  New problem
                </button>
              </div>

              <p className="text-base text-foreground leading-relaxed">
                {current.prompt}
              </p>

              {current.formula && (
                <div className="rounded-lg border border-border bg-background/40 p-3 font-mono text-sm text-foreground">
                  {current.formula}
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-2">
                {current.options.map((opt, i) => {
                  const isSelected = attempt.selected === i;
                  const isCorrect = i === current.correct;
                  let cls =
                    "border-border bg-background/40 hover:bg-muted/60 hover:border-muted-foreground/40";
                  if (attempt.graded) {
                    if (isCorrect)
                      cls =
                        "border-emerald-500/40 bg-emerald-500/10 text-emerald-400";
                    else if (isSelected)
                      cls =
                        "border-destructive/40 bg-destructive/10 text-destructive";
                    else cls = "border-border bg-background/40 opacity-60";
                  } else if (isSelected) {
                    cls = "border-primary bg-primary/10";
                  }
                  return (
                    <button
                      key={i}
                      disabled={attempt.graded}
                      onClick={() =>
                        setAttempt((a) => ({ ...a, selected: i }))
                      }
                      className={`text-left rounded-lg border p-3 text-sm transition-colors ${cls}`}
                    >
                      <span className="inline-flex items-center gap-2 w-full">
                        <span className="font-mono text-xs text-muted-foreground">
                          {String.fromCharCode(65 + i)}.
                        </span>
                        <span className="text-foreground flex-1">{opt}</span>
                        {attempt.graded && isCorrect && (
                          <Check
                            size={14}
                            className="text-emerald-400"
                            aria-hidden="true"
                          />
                        )}
                        {attempt.graded && isSelected && !isCorrect && (
                          <XIcon
                            size={14}
                            className="text-destructive"
                            aria-hidden="true"
                          />
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center gap-2">
                {!attempt.graded ? (
                  <button
                    onClick={submit}
                    disabled={attempt.selected === null}
                    className="h-10 px-4 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Check answer
                  </button>
                ) : (
                  <button
                    onClick={next}
                    className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
                  >
                    Next problem
                    <ArrowRight size={14} aria-hidden="true" />
                  </button>
                )}
              </div>

              {attempt.graded && (
                <div
                  role="status"
                  className={`rounded-lg border p-3 text-sm ${
                    attempt.selected === current.correct
                      ? "border-emerald-500/30 bg-emerald-500/5"
                      : "border-destructive/30 bg-destructive/5"
                  }`}
                >
                  <p
                    className={`font-medium ${
                      attempt.selected === current.correct
                        ? "text-emerald-400"
                        : "text-destructive"
                    }`}
                  >
                    {attempt.selected === current.correct
                      ? "Correct."
                      : `Correct answer: ${String.fromCharCode(65 + current.correct)}. ${current.options[current.correct]}`}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {current.insight}
                  </p>
                </div>
              )}
            </article>
          ) : (
            <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              No problems match your filters.
              <button
                onClick={() => {
                  setEnabledCategories(new Set(ALL_CATEGORIES));
                  setDifficulty("all");
                }}
                className="ml-2 inline-flex items-center gap-1 text-primary hover:underline"
              >
                <RefreshCw size={12} aria-hidden="true" />
                Reset filters
              </button>
            </div>
          )}
        </section>
      </Main>
    </div>
  );
}